'use client';

import TextareaWithButton from '@/components/features/questionnaire/components/QuestionTextArea';
import { ConversationResource } from '@/types';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { messageVariants } from '@/lib/helpers/animations/variants';
import { XIcon, ClipboardListIcon } from 'lucide-react';
import clsx from 'clsx';

// Local storage key for storing the dismissed state
const SUBMISSION_DISMISSED_KEY = 'jaqln_submission_confirmation_dismissed';

// Helper to check if we're in development mode
const isDevelopment = process.env.NODE_ENV === 'development';

export interface InputAreaProps {
  isQuestionnaireSubmitted: boolean;
  conversationData: ConversationResource | null | undefined;
  handleFormGoBack: () => void;
  inputValue: string;
  setInputValue: (value: string) => void;
  handlePromptSubmission: (text: string, formAnswerId?: string) => void;
  formAnswerId: string;
  t: any;
  isChatLoading: boolean;
  isStreaming: boolean;
  promptInputRef: React.RefObject<HTMLTextAreaElement>;
}

export const InputArea = React.memo((props: InputAreaProps) => {
  const {
    isQuestionnaireSubmitted,
    conversationData,
    handleFormGoBack,
    inputValue,
    setInputValue,
    handlePromptSubmission,
    formAnswerId,
    t,
    isChatLoading,
    isStreaming,
    promptInputRef,
  } = props;

  const [showSubmissionConfirmation, setShowSubmissionConfirmation] =
    useState(true);

  // Check localStorage on mount to see if user previously dismissed the message
  useEffect(() => {
    // Only check localStorage in development mode and in client-side environment
    if (isDevelopment && typeof window !== 'undefined') {
      const isDismissed =
        localStorage.getItem(SUBMISSION_DISMISSED_KEY) === 'true';
      if (isDismissed) {
        setShowSubmissionConfirmation(false);
      }
    }
  }, []);

  useEffect(() => {
    if (isChatLoading) {
      // In development mode, respect the user's preference
      if (isDevelopment && typeof window !== 'undefined') {
        const isDismissed =
          localStorage.getItem(SUBMISSION_DISMISSED_KEY) === 'true';
        if (!isDismissed) {
          setTimeout(() => {
            setShowSubmissionConfirmation(true);
          }, 1000);
        }
      } else {
        // In production, always show the confirmation
        setTimeout(() => {
          setShowSubmissionConfirmation(true);
        }, 1000);
      }
    }
  }, [isChatLoading]);

  // Handle dismissing the confirmation message
  const handleDismissConfirmation = () => {
    setShowSubmissionConfirmation(false);

    // In development mode, save the dismissed state to localStorage
    if (isDevelopment && typeof window !== 'undefined') {
      localStorage.setItem(SUBMISSION_DISMISSED_KEY, 'true');
    }
  };
  const MAX_LENGTH = 1000;

  return (
    <>
      {isQuestionnaireSubmitted &&
        showSubmissionConfirmation &&
        (!conversationData ||
          (conversationData?.advices?.length ?? 0) === 0) && (
          <motion.div
            variants={messageVariants}
            className='mx-2 mb-2 inline-flex flex-shrink-0'
          >
            <div className='mx-0 mt-2 flex flex-shrink-0 items-center rounded-lg border border-gray-300 bg-white px-2 py-2 shadow-lg dark:border-gray-700 dark:bg-gray-900 dark:shadow-gray-900/50 sm:mx-2 sm:px-4'>
              <div>
                <p className='text-sm text-gray-600 dark:text-gray-300'>
                  {t(
                    'formQuestionnaire.messages.submissionConfirmation.confirmation'
                  )}
                </p>
                <p
                  className='flex cursor-pointer gap-1 text-sm text-purple-600 hover:underline dark:text-purple-400'
                  onClick={handleFormGoBack}
                >
                  {t(
                    'formQuestionnaire.messages.submissionConfirmation.instructions1'
                  )}
                  <ClipboardListIcon className='h-5 w-5' />
                  {t(
                    'formQuestionnaire.messages.submissionConfirmation.instructions2'
                  )}
                </p>
              </div>
              <button
                className='ml-2 cursor-pointer text-red-600 hover:font-bold dark:text-red-400 sm:ml-4'
                onClick={handleDismissConfirmation}
                aria-label='Dismiss confirmation'
              >
                <XIcon className='h-4 w-4 sm:h-5 sm:w-5' />
              </button>
            </div>
          </motion.div>
        )}
      <motion.div
        variants={messageVariants}
        className='m-2 rounded-none border-x-0 border-purple-100 bg-gray-50 shadow-lg dark:border-purple-900/50 dark:bg-gray-900/50 dark:shadow-gray-900/20 sm:mx-4 sm:my-2 sm:rounded-lg sm:border'
      >
        <TextareaWithButton
          shouldTextAreaBeAvailable={
            isQuestionnaireSubmitted ||
            (conversationData?.advices?.length ?? 0) > 0
          }
          inputValue={inputValue}
          setInputValue={setInputValue}
          handlePromptSubmission={handlePromptSubmission}
          handleFormGoBack={handleFormGoBack}
          formAnswerId={formAnswerId}
          t={t}
          isLoading={isChatLoading || isStreaming}
          isDisabled={isStreaming}
          promptInputRef={promptInputRef}
        />
      </motion.div>
      <div
        className={`m-2 flex items-center text-sm ${
          inputValue.length === MAX_LENGTH
            ? 'text-red-600 dark:text-red-400'
            : 'text-gray-500 dark:text-gray-400'
        }`}
      >
        <span>
          {t('chat.input.chatCharacterLimitNotice')} {inputValue.length}/
          {MAX_LENGTH}
        </span>
      </div>
    </>
  );
});

InputArea.displayName = 'InputArea';
